import React, { useState, useEffect } from 'react';
import { apiClient } from '../../../utils/apiClient';
import { Upload, Palette, Type, Save, Eye } from 'lucide-react';

const Branding = () => {
  const [branding, setBranding] = useState({
    primary_color: '#3B82F6',
    secondary_color: '#1E40AF',
    font_family: 'Inter',
    logo_path: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/branding');
      if (response.data.status === 'success') {
        setBranding(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiClient.put('/branding', {
        primary_color: branding.primary_color,
        secondary_color: branding.secondary_color,
        font_family: branding.font_family
      });
      if (response.data.status === 'success') {
        setBranding(response.data.data);
        alert('Branding updated successfully!');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      alert('Error saving branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      alert('File size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/branding/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        setBranding(prev => ({
          ...prev,
          logo_path: response.data.data.logo_path
        }));
        alert('Logo uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Error uploading logo');
    } finally {
      setUploading(false);
    }
  };

  const fontFamilies = [
    'Inter',
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Poppins',
    'Source Sans Pro',
    'Nunito',
    'Raleway',
    'Ubuntu'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Branding</h1>
          <p className="mt-1 text-sm text-gray-500">
            Customize your business appearance and branding
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setPreview(!preview)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Eye className="h-4 w-4 mr-2" />
            {preview ? 'Hide Preview' : 'Show Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Branding Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Brand Colors</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                />
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={branding.primary_color}
                  onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Color
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
                  value={branding.secondary_color}
                  onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                />
                <input
                  type="text"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={branding.secondary_color}
                  onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Family
              </label>
              <select
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={branding.font_family}
                onChange={(e) => setBranding({ ...branding, font_family: e.target.value })}
              >
                {fontFamilies.map((font) => (
                  <option key={font} value={font} style={{ fontFamily: font }}>
                    {font}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Logo Upload */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Business Logo</h3>
          
          <div className="space-y-4">
            {branding.logo_path && (
              <div className="text-center">
                <img
                  src={`http://localhost:8000/uploads/${branding.logo_path}`}
                  alt="Current logo"
                  className="mx-auto h-32 w-32 object-contain border border-gray-300 rounded-lg"
                />
                <p className="mt-2 text-sm text-gray-500">Current logo</p>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload New Logo
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="logo-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id="logo-upload"
                        name="logo-upload"
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={uploading}
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF up to 5MB
                  </p>
                </div>
              </div>
              {uploading && (
                <div className="mt-2 text-center">
                  <div className="inline-flex items-center text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Uploading...
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Preview</h3>
          <div 
            className="border rounded-lg p-6"
            style={{
              fontFamily: branding.font_family,
              '--primary-color': branding.primary_color,
              '--secondary-color': branding.secondary_color
            }}
          >
            <div className="flex items-center justify-between mb-6">
              {branding.logo_path && (
                <img
                  src={`http://localhost:8000/uploads/${branding.logo_path}`}
                  alt="Logo"
                  className="h-12 w-auto"
                />
              )}
              <h1 
                className="text-2xl font-bold"
                style={{ color: branding.primary_color }}
              >
                Your Business Name
              </h1>
            </div>
            
            <div className="space-y-4">
              <div 
                className="p-4 rounded-lg"
                style={{ backgroundColor: `${branding.primary_color}20` }}
              >
                <h2 
                  className="text-lg font-semibold mb-2"
                  style={{ color: branding.primary_color }}
                >
                  Sample Invoice
                </h2>
                <p className="text-gray-700">
                  This is how your invoices will look with your branding.
                </p>
              </div>
              
              <div className="flex space-x-2">
                <button
                  className="px-4 py-2 rounded-md text-white font-medium"
                  style={{ backgroundColor: branding.primary_color }}
                >
                  Primary Button
                </button>
                <button
                  className="px-4 py-2 rounded-md text-white font-medium"
                  style={{ backgroundColor: branding.secondary_color }}
                >
                  Secondary Button
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Branding;
